import React from "react";

import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";
import { createActivationKeyDownHandler } from "@Front/Utils/interactiveElementA11y";
import classes from "./classes.module.scss";
import { IItem } from "./MenuItem";
import { MenuSubMenuItems } from "./MenuSubMenuItems";

type MenuChromeProps = {
	children: React.ReactNode;
	openingSide: "left" | "right" | "center";
	openOnHover: boolean | undefined;
	toggle: () => void;
	handleMouseEnter: () => void;
	handleMouseLeave: () => void;
	iconRef: React.RefObject<HTMLDivElement | null>;
	subMenuRef: React.RefObject<HTMLDivElement | null>;
	shouldShowSubMenu: boolean;
	items: IItem[];
	isSuperAdmin: boolean;
	close: () => void;
};

function MenuChromeSubMenuPanel(props: Pick<MenuChromeProps, "shouldShowSubMenu" | "subMenuRef" | "openingSide" | "items" | "isSuperAdmin" | "close">): React.JSX.Element | null {
	const { shouldShowSubMenu, subMenuRef, openingSide, items, isSuperAdmin, close } = props;
	if (!shouldShowSubMenu) {
		return null;
	}
	return (
		<div className={classes["sub-menu"]} ref={subMenuRef} data-opening-side={openingSide}>
			<MenuSubMenuItems items={items} isSuperAdmin={isSuperAdmin} close={close} />
		</div>
	);
}

function MenuChromeMainTrigger(props: {
	children: React.ReactNode;
	iconRef: React.RefObject<HTMLDivElement | null>;
	toggle: () => void;
	shouldShowSubMenu: boolean;
}): React.JSX.Element {
	const { children, iconRef, toggle, shouldShowSubMenu } = props;
	const keyHandlers = createActivationKeyDownHandler(() => toggle());
	if (shouldShowSubMenu) {
		return (
			<div
				ref={iconRef}
				className={classes["main"]}
				role="button"
				tabIndex={0}
				onClick={toggle}
				onKeyDown={keyHandlers}
				aria-label={COMMON_UI_I18N.iconButtonAccessibilityLabels.moreActions}
				aria-expanded="true"
				aria-haspopup="true">
				{children}
			</div>
		);
	}
	return (
		<div
			ref={iconRef}
			className={classes["main"]}
			role="button"
			tabIndex={0}
			onClick={toggle}
			onKeyDown={keyHandlers}
			aria-label={COMMON_UI_I18N.iconButtonAccessibilityLabels.moreActions}
			aria-expanded="false"
			aria-haspopup="true">
			{children}
		</div>
	);
}

export function MenuChrome(props: MenuChromeProps): React.JSX.Element {
	return (
		<div
			className={classes["root"]}
			onMouseEnter={props.openOnHover ? props.handleMouseEnter : undefined}
			onMouseLeave={props.openOnHover ? props.handleMouseLeave : undefined}>
			<MenuChromeMainTrigger
				iconRef={props.iconRef}
				toggle={props.toggle}
				shouldShowSubMenu={props.shouldShowSubMenu}>
				{props.children}
			</MenuChromeMainTrigger>
			<MenuChromeSubMenuPanel
				shouldShowSubMenu={props.shouldShowSubMenu}
				subMenuRef={props.subMenuRef}
				openingSide={props.openingSide}
				items={props.items}
				isSuperAdmin={props.isSuperAdmin}
				close={props.close}
			/>
		</div>
	);
}
